/**
 * Botanica Philosophy water renderer.
 * Three.js r185 / 0.185.1, lazy-loaded only when Philosophy nears the viewport. The shader draws
 * one teardrop field, two assembly satellites, and a pointer-driven hover particle that stretches
 * out of the settled water. The approved raster stays visible unless the first WebGL frame renders.
 */

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  varying vec2 vUv;
  uniform vec2 uResolution;
  uniform vec2 uPointer;
  uniform float uPointerStrength;
  uniform float uTime;
  uniform float uAssembly;

  float smoothMin(float a, float b, float radius) {
    float h = clamp(0.5 + 0.5 * (b - a) / radius, 0.0, 1.0);
    return mix(b, a, h) - radius * h * (1.0 - h);
  }

  float segmentDistance(vec2 point, vec2 start, vec2 end) {
    vec2 segment = end - start;
    float position = clamp(dot(point - start, segment) / max(dot(segment, segment), 0.0001), 0.0, 1.0);
    return length(point - (start + segment * position));
  }

  float dropletDistance(vec2 point) {
    /* Match the approved drop-actives.png silhouette: one calm apex, tapered shoulders and a
       broad rounded base. A very small horizontal wave keeps the water alive without changing
       the recognizable Botanica droplet shape. */
    vec2 bodyPoint = point - vec2(-0.012, -0.025);
    bodyPoint.x += sin((bodyPoint.y + 0.22) * 3.0 + uTime * 0.44) * 0.020;

    float bottom = -0.76 - sin(uTime * 0.31) * 0.008;
    float top = 0.78 + sin(uTime * 0.37 + 1.2) * 0.008;
    float position = clamp((bodyPoint.y - bottom) / (top - bottom), 0.0, 1.0);
    float arch = pow(max(sin(position * 3.14159265), 0.0), 0.56);
    float upperTaper = mix(1.04, 0.72, smoothstep(0.48, 1.0, position));
    float membrane = 1.0 + sin(position * 5.4 + uTime * 0.52) * 0.018 +
      cos(position * 8.1 - uTime * 0.29) * 0.008;
    float halfWidth = 0.61 * arch * upperTaper * membrane;
    float horizontal = abs(bodyPoint.x) - halfWidth;
    float vertical = max(bottom - bodyPoint.y, bodyPoint.y - top);
    return max(horizontal, vertical) * 0.72;
  }

  float waterDistance(vec2 point) {
    float assemble = smoothstep(0.0, 1.0, uAssembly);
    float mainScale = mix(0.82, 1.06, assemble);
    float distanceField = dropletDistance(point / mainScale) * mainScale;

    vec2 firstStart = vec2(-0.92, 0.42);
    vec2 firstHome = vec2(-0.27, 0.14);
    vec2 secondStart = vec2(0.86, -0.46);
    vec2 secondHome = vec2(0.26, -0.18);

    vec2 firstDrift = vec2(
      sin(uTime * 0.54) * 0.025,
      cos(uTime * 0.42) * 0.032
    );
    vec2 secondDrift = vec2(
      cos(uTime * 0.46) * 0.022,
      sin(uTime * 0.38) * 0.028
    );

    vec2 firstPosition = mix(firstStart, firstHome, assemble) +
      firstDrift * assemble + uPointer * vec2(0.15, 0.10) * assemble;
    vec2 secondPosition = mix(secondStart, secondHome, assemble) +
      secondDrift * assemble + uPointer * vec2(-0.11, 0.13) * assemble;

    float firstRadius = mix(0.13, 0.17, assemble);
    float secondRadius = mix(0.10, 0.13, assemble);
    float first = length(point - firstPosition) - firstRadius;
    float second = length(point - secondPosition) - secondRadius;

    distanceField = smoothMin(distanceField, first, mix(0.035, 0.13, assemble));
    distanceField = smoothMin(distanceField, second, mix(0.028, 0.105, assemble));

    /* STEM shifts its existing field toward the pointer before adding a new pointer metaball.
       Recreate that relationship as a membrane lobe, a tapering liquid neck and the satellite.
       The neck remains continuous through most of the pull and breaks only at the outer reach. */
    float pointerMagnitude = length(uPointer);
    vec2 pointerDirection = uPointer / max(pointerMagnitude, 0.001);
    float edgeReach = mix(0.58, 0.76, abs(pointerDirection.y));
    float boundaryProgress = pointerMagnitude / max(edgeReach, 0.001);
    float settled = smoothstep(0.58, 1.0, assemble) * uPointerStrength;
    /* The centre is a true dead zone. The membrane begins following only near its inside edge;
       the neck and satellite do not begin forming until the pointer crosses that boundary. */
    float membraneInteraction = settled * smoothstep(0.56, 0.98, boundaryProgress);
    float extensionInteraction = settled * smoothstep(0.92, 1.18, boundaryProgress);
    float pointerTravel = smoothstep(0.92, 1.72, boundaryProgress);
    float release = smoothstep(0.72, 0.98, pointerTravel);
    vec2 membranePosition = pointerDirection * (edgeReach - 0.055);
    vec2 hoverPosition = pointerDirection * (edgeReach + mix(0.035, 0.48, pointerTravel));
    vec2 neckEnd = mix(membranePosition, hoverPosition, 0.82);

    float membraneRadius = mix(0.070, 0.145, pointerTravel) * membraneInteraction * mix(1.0, 0.28, release);
    float membrane = length(point - membranePosition) - membraneRadius +
      (1.0 - membraneInteraction) * 0.48;
    distanceField = smoothMin(distanceField, membrane, max(0.002, 0.105 * membraneInteraction));

    /* Preserve the organic pull while the particle is attached, then fully remove the filament
       after separation. Leaving even a 0.01-radius capsule caused the thin line at full reach. */
    float neckVisibility = 1.0 - smoothstep(0.72, 0.92, pointerTravel);
    float neckRadius = mix(0.078, 0.026, release) * extensionInteraction * neckVisibility;
    float neck = segmentDistance(point, membranePosition, neckEnd) - neckRadius +
      (1.0 - extensionInteraction) * 0.48 + (1.0 - neckVisibility) * 0.55;
    distanceField = smoothMin(distanceField, neck,
      max(0.002, mix(0.085, 0.022, release) * extensionInteraction * neckVisibility));

    float hoverRadius = mix(0.055, 0.170, pointerTravel) * extensionInteraction;
    float hover = length(point - hoverPosition) - hoverRadius;
    distanceField = smoothMin(distanceField, hover,
      max(0.002, mix(0.095, 0.025, release) * extensionInteraction));
    return distanceField;
  }

  void main() {
    /* The canvas extends 25% beyond every edge so a released particle never meets a square clip.
       Expanding shader space by the same 1.5 factor preserves the approved droplet's pixel size. */
    vec2 point = (vUv * 2.0 - 1.0) * 1.5;
    point.x *= uResolution.x / max(uResolution.y, 1.0);

    float distanceField = waterDistance(point);
    float antialias = max(fwidth(distanceField) * 1.45, 0.0025);
    float silhouette = 1.0 - smoothstep(-antialias, antialias, distanceField);
    if (silhouette < 0.002) discard;

    float epsilon = 0.004;
    vec2 normal = normalize(vec2(
      waterDistance(point + vec2(epsilon, 0.0)) - waterDistance(point - vec2(epsilon, 0.0)),
      waterDistance(point + vec2(0.0, epsilon)) - waterDistance(point - vec2(0.0, epsilon))
    ));

    float rim = 1.0 - smoothstep(0.0, 0.07, abs(distanceField));
    float depth = smoothstep(0.0, 0.42, -distanceField);
    float upperLight = pow(max(dot(normal, normalize(vec2(-0.66, 0.75))), 0.0), 7.0);
    float lowerLight = pow(max(dot(normal, normalize(vec2(0.72, -0.69))), 0.0), 11.0);
    float innerGlow = pow(max(0.0, 1.0 - length(point - vec2(-0.16, 0.18))), 3.0);

    vec3 warmWater = vec3(0.945, 0.920, 0.865);
    vec3 clearWater = vec3(0.885, 0.945, 0.975);
    vec3 color = mix(clearWater, warmWater, 0.52 + point.y * 0.08);
    vec3 iridescence = vec3(
      0.78 + 0.10 * sin(point.y * 7.0 + uTime * 0.22),
      0.86 + 0.08 * sin(point.y * 7.0 + 2.1),
      0.94
    );

    color = mix(color, iridescence, rim * 0.42);
    color += vec3(1.0, 0.99, 0.96) * upperLight * 0.32;
    color += vec3(0.84, 0.92, 1.0) * lowerLight * 0.20;
    color += vec3(1.0, 0.96, 0.86) * innerGlow * depth * 0.06;
    color -= vec3(0.08, 0.075, 0.055) * depth * 0.09;

    float alpha = silhouette * clamp(0.24 + rim * 0.52 + upperLight * 0.10, 0.0, 0.86);
    gl_FragColor = vec4(color, alpha);
  }
`;

function initWaterRenderer(section, THREE) {
  if (!section || section.dataset.bbWaterReady ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const host = section.querySelector('[data-bb-actives-water]');
  const canvas = section.querySelector('[data-bb-actives-water-canvas]');
  const stage = section.querySelector('[data-bb-actives-stage]');
  const media = section.querySelector('[data-bb-actives-media]');
  if (!host || !canvas || !stage || !media) return;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      powerPreference: 'high-performance'
    });
  } catch (error) {
    return;
  }

  section.dataset.bbWaterReady = '1';
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const uniforms = {
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPointer: { value: new THREE.Vector2(0, 0) },
    uPointerStrength: { value: 0 },
    uTime: { value: 0 },
    uAssembly: { value: 0 }
  };
  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const geometry = new THREE.PlaneGeometry(2, 2);
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    depthTest: false,
    depthWrite: false
  });
  scene.add(new THREE.Mesh(geometry, material));

  let visible = false;
  let assembled = false;
  let assemblyStart = 0;
  let animationFrame = 0;
  let pointerX = 0;
  let pointerY = 0;
  let pointerTargetX = 0;
  let pointerTargetY = 0;
  let pointerStrength = 0;
  let pointerTargetStrength = 0;
  const pointerFine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function resize() {
    const bounds = host.getBoundingClientRect();
    const width = Math.max(1, Math.round(bounds.width));
    const height = Math.max(1, Math.round(bounds.height));
    renderer.setSize(width, height, false);
    uniforms.uResolution.value.set(width, height);
  }

  function requestRender() {
    if (!visible || document.hidden || animationFrame) return;
    animationFrame = requestAnimationFrame(render);
  }

  function render(now) {
    animationFrame = 0;
    if (!visible || document.hidden) return;

    if (!assemblyStart) assemblyStart = now;
    if (!assembled) {
      const rawProgress = Math.min((now - assemblyStart) / 4000, 1);
      uniforms.uAssembly.value = rawProgress * rawProgress * (3 - 2 * rawProgress);
      assembled = rawProgress >= 1;
    }

    pointerX += (pointerTargetX - pointerX) * 0.075;
    pointerY += (pointerTargetY - pointerY) * 0.075;
    pointerStrength += (pointerTargetStrength - pointerStrength) * 0.085;
    uniforms.uPointer.value.set(pointerX, pointerY);
    uniforms.uPointerStrength.value = pointerStrength;
    uniforms.uTime.value = now * 0.001;
    renderer.render(scene, camera);

    if (!section.classList.contains('is-webgl-ready')) {
      section.classList.add('is-webgl-ready');
    }
    requestRender();
  }

  if (pointerFine) {
    stage.addEventListener('pointermove', (event) => {
      const bounds = media.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      pointerTargetX = THREE.MathUtils.clamp(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -1, 1);
      pointerTargetY = THREE.MathUtils.clamp(-(((event.clientY - bounds.top) / bounds.height) * 2 - 1), -1, 1);
      pointerTargetStrength = 1;
      requestRender();
    }, { passive: true });
    stage.addEventListener('pointerleave', () => {
      pointerTargetX = 0;
      pointerTargetY = 0;
      pointerTargetStrength = 0;
    }, { passive: true });
  }

  canvas.addEventListener('webglcontextlost', (event) => {
    event.preventDefault();
    visible = false;
    section.classList.remove('is-webgl-ready');
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (visible) {
        resize();
        requestRender();
      } else if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
    }, { rootMargin: '15% 0px 15% 0px' });
    observer.observe(section);
  } else {
    visible = true;
    resize();
    requestRender();
  }

  if ('ResizeObserver' in window) {
    new ResizeObserver(() => {
      resize();
      requestRender();
    }).observe(host);
  } else {
    window.addEventListener('resize', () => {
      resize();
      requestRender();
    }, { passive: true });
  }

  document.addEventListener('visibilitychange', requestRender);
  resize();
}

let threeModulePromise;

function loadThree() {
  if (!threeModulePromise) threeModulePromise = import('./three.module.min.js');
  return threeModulePromise;
}

function queueWaterRenderer(section) {
  if (!section || section.dataset.bbWaterQueued ||
      !section.querySelector('[data-bb-actives-water]') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  section.dataset.bbWaterQueued = '1';

  const load = () => {
    loadThree().then((THREE) => initWaterRenderer(section, THREE)).catch(() => {
      section.classList.remove('is-webgl-ready');
    });
  };

  if ('IntersectionObserver' in window) {
    const preloadObserver = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      preloadObserver.disconnect();
      load();
    }, { rootMargin: '60% 0px 60% 0px' });
    preloadObserver.observe(section);
  } else {
    load();
  }
}

function initAllWaterRenderers(root = document) {
  root.querySelectorAll('[data-bb-actives]').forEach(queueWaterRenderer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllWaterRenderers());
} else {
  initAllWaterRenderers();
}

document.addEventListener('shopify:section:load', (event) => initAllWaterRenderers(event.target));
